import {
    Input,
    Col,
    Descriptions,
    Row,
    Comment,
    List,
    Form,
    Button,
    Collapse,
    Modal,
    Spin
} from "antd";
import {useFormik} from "formik";
import React, {useRef, useState} from "react";
import * as yup from "yup";
import {useTranslation} from "react-i18next";
import {useFetch} from "../../../hooks/useFetch";
import {useStore} from "../../../hooks/useStore";
import ObjectionRevisionModal from "./ObjectionRevisionModal/ObjectionRevisionModal";
import {StyledDivider} from "./ObjectionDetailStyles";
import {formatMomentDate} from "../../../utils/helpers";
export const ObjectionDetailModal = ({
    objectionId,
    isVisible,
    setVisible,
    setReloadData,
    reloadData
}) => {
    const {t} = useTranslation(["shiftScore", "objectionDetail"]);
    const stores = useStore();
    const [recall, setRecall] = useState(true);
    const recallRef = useRef(recall);
    const [loading, setLoading] = useState(false);
    const isCustomer = stores.meStore.checkRole("isNonApiIntegratedCustomer");
    const [revisionModalVisibility, setRevisionModalVisibility] = useState(false);

    recallRef.current = recall;
    const [objectionLoading, objection, setObjection, setObjectionLoading] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.shiftStore.getShiftScoreObjection.bind(null, {
            objectionId
        }),
        dependencies: [recall, objectionId],
        condition: objectionId
    });

    const formik = useFormik({
        initialValues: {text: "", objectionId: objectionId},
        validationSchema: yup.object().shape({
            text: yup.string().required(t("validation.required", {ns: "common"}))
        }),
        onSubmit: async values => {
            setLoading(true);
            await stores.shiftStore.createShiftScoreObjectionNote(values);
            await resetForm();
            setLoading(false);
            await setRecall(!recallRef.current);
        },
        enableReinitialize: true
    });

    const {values, errors, touched, handleChange, handleSubmit, resetForm, setFieldValue} = formik;

    const closeModal = async ({reloadData = false}) => {
        if (reloadData) {
            await setRecall(!recallRef.current);
        }
        setRevisionModalVisibility(false);
    };

    const approveObjection = () => {
        stores.appStore.openConfirmModal({
            title: t("modal.confirmTitle", {ns: "common"}),
            content: t("objectionDetail.approveModal.content", {ns: "objectionDetail"}),
            onOk: async () => {
                setLoading(true);
                await stores.shiftStore.updateShiftScoreObjectionApproval({
                    objectionId: objectionId,
                    approval: "approved"
                });
                setLoading(false);
                await setRecall(!recallRef.current);
                setVisible();
                setReloadData(!reloadData);
            }
        });
    };

    const declineObjection = () => {
        stores.appStore.openConfirmModal({
            title: t("modal.confirmTitle", {ns: "common"}),
            content: t("objectionDetail.declineModal.content", {ns: "objectionDetail"}),
            onOk: async () => {
                setLoading(true);
                await stores.shiftStore.updateShiftScoreObjectionApproval({
                    objectionId: objectionId,
                    approval: "declined"
                });
                setLoading(false);
                await setRecall(!recallRef.current);
                setVisible();
                setReloadData(!reloadData);
            }
        });
    };

    const isReadOnlyForOperationRole = stores.meStore.checkRole("hideForOperationRole");

    const permisions = {
        addNote:
            !stores.meStore.checkRole("isOperationUser") &&
            !isReadOnlyForOperationRole &&
            ["inReview"].includes(objection?.shiftReport?.status),
        viewApprovalButtons:
            !stores.meStore.checkRole("isOperationUser") &&
            !isReadOnlyForOperationRole &&
            stores.meStore.checkRole("notVisibleForCompanyUser") &&
            ["inReview"].includes(objection?.shiftReport?.status),
        decline:
            stores.meStore.checkRole("notVisibleForCompanyUser") &&
            !isReadOnlyForOperationRole &&
            ["inReview"].includes(objection?.shiftReport?.status),
        revision:
            stores.meStore.checkRole("notVisibleForCompanyUser") &&
            !isReadOnlyForOperationRole &&
            ["inReview"].includes(objection?.shiftReport?.status),
        approve:
            stores.meStore.checkRole("notVisibleForCompanyUser") &&
            !isReadOnlyForOperationRole &&
            ["inReview"].includes(objection?.shiftReport?.status),
        showPrivateField: stores.meStore.checkRole("notVisibleForCompanyUser")
    };

    const renderFooter = () => {
        const decline = permisions.decline ? (
            <Button
                type="primary"
                danger
                disabled={loading}
                loading={loading}
                onClick={declineObjection}
            >
                {t("objectionDetail.actions.decline", {
                    ns: "objectionDetail"
                })}
            </Button>
        ) : (
            ""
        );
        const revision = permisions.revision ? (
            <Button
                disabled={loading}
                loading={loading}
                onClick={() => setRevisionModalVisibility(true)}
            >
                {t("objectionDetail.actions.revision", {
                    ns: "objectionDetail"
                })}
            </Button>
        ) : (
            ""
        );

        const approve = permisions.approve ? (
            <Button type="primary" disabled={loading} loading={loading} onClick={approveObjection}>
                {t("objectionDetail.actions.approve", {
                    ns: "objectionDetail"
                })}
            </Button>
        ) : (
            ""
        );

        const approvalButtons = permisions.viewApprovalButtons ? [approve, decline, revision] : [];

        return [...approvalButtons];
    };
    return (
        <>
            <Modal
                width={1000}
                bodyStyle={{height: "500px", overflowY: "scroll"}}
                visible={isVisible}
                centered
                title={t("objectionDetail.objectionDetail", {
                    ns: "objectionDetail"
                })}
                data-testid="objectionDetailModal"
                footer={renderFooter()}
                onCancel={() => {
                    resetForm();
                    setVisible();
                }}
                className={isCustomer ? "shift-score-objection-modal" : ""}
            >
                <ObjectionRevisionModal
                    shiftScore={objection ? objection?.shiftReport : undefined}
                    objection={objection}
                    onCancel={() => closeModal({reloadData: false})}
                    closeModal={() => closeModal({reloadData: true})}
                    visibility={revisionModalVisibility}
                />
                {objectionLoading && (
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100%"
                        }}
                    >
                        <Spin size="large" tip={"Yükleniyor..."} />
                    </div>
                )}
                {objection && !objectionLoading && (
                    <>
                        <Row gutter={[16, 32]}>
                            <Col span={24}>
                                <Descriptions column={3} bordered>
                                    <Descriptions.Item
                                        span={3}
                                        label={`${t("fields.expectedWorkTime", {
                                            ns: "common"
                                        })}(${t("units.minute.short", {ns: "common"})})`}
                                    >
                                        {objection.workTimeMinute}
                                        {objection.revision && (
                                            <>
                                                {" ~> "}
                                                <b>{objection.revision.workTimeMinute}</b>
                                            </>
                                        )}
                                    </Descriptions.Item>
                                    <Descriptions.Item
                                        span={3}
                                        label={`${t("fields.expectedPackageCount", {
                                            ns: "common"
                                        })}`}
                                        contentStyle={{minWidth: "300px"}}
                                    >
                                        {objection.packageCount}
                                        {objection.revision && (
                                            <>
                                                {" ~> "}
                                                <b>{objection.revision.packageCount}</b>
                                            </>
                                        )}
                                    </Descriptions.Item>
                                    <Descriptions.Item
                                        span={3}
                                        label={t("fields.objectedBy", {ns: "common"})}
                                    >
                                        {objection?.user?.fullName}
                                    </Descriptions.Item>
                                    <Descriptions.Item
                                        span={3}
                                        label={t("fields.objectionDescription", {ns: "common"})}
                                    >
                                        {objection?.notes && objection?.notes[0]
                                            ? objection.notes[0].text
                                            : "-"}
                                    </Descriptions.Item>
                                </Descriptions>
                            </Col>

                            {objection?.shiftReport && (
                                <Col span={24}>
                                    <Collapse>
                                        <Collapse.Panel
                                            header={t("objectionDetail.reportInfo", {
                                                ns: "objectionDetail"
                                            })}
                                            key="1"
                                        >
                                            <Descriptions column={3} bordered>
                                                <Descriptions.Item
                                                    span={3}
                                                    label={t("fields.date", {ns: "common"})}
                                                >
                                                    {formatMomentDate(
                                                        objection.shiftReport.reportDate
                                                    )}
                                                </Descriptions.Item>
                                                <Descriptions.Item
                                                    span={3}
                                                    label={t("fields.courier", {ns: "common"})}
                                                >
                                                    {objection.shiftReport.fullname}
                                                </Descriptions.Item>
                                                {permisions.showPrivateField && (
                                                    <Descriptions.Item
                                                        span={3}
                                                        label={t("fields.courierIdNo", {
                                                            ns: "common"
                                                        })}
                                                    >
                                                        {objection.shiftReport.tckn}
                                                    </Descriptions.Item>
                                                )}
                                                {permisions.showPrivateField && (
                                                    <Descriptions.Item
                                                        span={3}
                                                        label={t("fields.courierPhone", {
                                                            ns: "common"
                                                        })}
                                                    >
                                                        {objection.shiftReport.cellPhone}
                                                    </Descriptions.Item>
                                                )}
                                                <Descriptions.Item
                                                    span={3}
                                                    label={t("fields.company", {ns: "common"})}
                                                >
                                                    {objection.shiftReport.companyName}
                                                </Descriptions.Item>
                                                {permisions.showPrivateField && (
                                                    <Descriptions.Item
                                                        span={3}
                                                        label={t("fields.companyCode", {
                                                            ns: "common"
                                                        })}
                                                    >
                                                        {objection.shiftReport.companyCode}
                                                    </Descriptions.Item>
                                                )}
                                                <Descriptions.Item
                                                    span={3}
                                                    label={t("fields.subcompany", {ns: "common"})}
                                                >
                                                    {objection.shiftReport.subCompanyName}
                                                </Descriptions.Item>
                                                {permisions.showPrivateField && (
                                                    <Descriptions.Item
                                                        span={3}
                                                        label={t("fields.subCompanyCode", {
                                                            ns: "common"
                                                        })}
                                                    >
                                                        {objection.shiftReport.subCompanyCode}
                                                    </Descriptions.Item>
                                                )}
                                                <Descriptions.Item
                                                    span={3}
                                                    label={t("shiftScore.shiftStartTime", {
                                                        ns: "shiftScore"
                                                    })}
                                                >
                                                    {objection.shiftReport.shiftStartTime}
                                                </Descriptions.Item>
                                                <Descriptions.Item
                                                    span={3}
                                                    label={t("shiftScore.shiftEndTime", {
                                                        ns: "shiftScore"
                                                    })}
                                                >
                                                    {objection.shiftReport.shiftEndTime}
                                                </Descriptions.Item>
                                                <Descriptions.Item
                                                    span={3}
                                                    label={t("shiftScore.startTime", {
                                                        ns: "shiftScore"
                                                    })}
                                                >
                                                    {objection.shiftReport.startTime}
                                                </Descriptions.Item>
                                                <Descriptions.Item
                                                    span={3}
                                                    label={t("shiftScore.endTime", {
                                                        ns: "shiftScore"
                                                    })}
                                                >
                                                    {objection.shiftReport.endTime}
                                                </Descriptions.Item>
                                                <Descriptions.Item
                                                    span={3}
                                                    label={`${t("shiftScore.workTime", {
                                                        ns: "shiftScore"
                                                    })} - (
                                        ${t("units.minute.short", {ns: "common"})})`}
                                                >
                                                    {objection.shiftReport.workTimeMinute}
                                                </Descriptions.Item>
                                                {permisions.showPrivateField && (
                                                    <Descriptions.Item
                                                        span={3}
                                                        label={` ${t("shiftScore.busyTimeMinute", {
                                                            ns: "shiftScore"
                                                        })} - (
                                        ${t("units.minute.short", {ns: "common"})})`}
                                                    >
                                                        {objection.shiftReport.busyTimeMinute}
                                                    </Descriptions.Item>
                                                )}
                                                <Descriptions.Item
                                                    span={3}
                                                    label={t("shiftScore.packageCount", {
                                                        ns: "shiftScore"
                                                    })}
                                                >
                                                    {objection.shiftReport.packageCount}
                                                </Descriptions.Item>
                                            </Descriptions>
                                        </Collapse.Panel>
                                    </Collapse>
                                </Col>
                            )}

                            {objection?.notes && (
                                <Col span={24}>
                                    <List
                                        className="comment-list"
                                        header={
                                            <>
                                                <h2>
                                                    {t("objectionDetail.notes", {
                                                        ns: "objectionDetail"
                                                    })}
                                                </h2>
                                            </>
                                        }
                                        itemLayout="horizontal"
                                        dataSource={objection.notes}
                                        renderItem={(note, item) => (
                                            <>
                                                {!note.actionKey && (
                                                    <li>
                                                        <Comment
                                                            author={note?.user?.fullName || "-"}
                                                            content={note?.text || "-"}
                                                            datetime={
                                                                formatMomentDate(note?.createdAt) ||
                                                                undefined
                                                            }
                                                        />
                                                        <StyledDivider />
                                                    </li>
                                                )}
                                                {note.actionKey === "declined" && (
                                                    <h4>
                                                        <Comment
                                                            content={
                                                                <i>
                                                                    {t(
                                                                        "objectionDetail.automatedNotes.declined",
                                                                        {
                                                                            ns: "objectionDetail",
                                                                            userName:
                                                                                note.user.fullName
                                                                        }
                                                                    )}
                                                                </i>
                                                            }
                                                            datetime={
                                                                formatMomentDate(note?.createdAt) ||
                                                                undefined
                                                            }
                                                        />

                                                        <StyledDivider />
                                                    </h4>
                                                )}
                                                {note.actionKey === "approved" && (
                                                    <h4>
                                                        <Comment
                                                            content={
                                                                <i>
                                                                    {t(
                                                                        "objectionDetail.automatedNotes.approved",
                                                                        {
                                                                            ns: "objectionDetail",
                                                                            userName:
                                                                                note.user.fullName
                                                                        }
                                                                    )}
                                                                    <br />
                                                                    {t(
                                                                        "objectionDetail.automatedNotes.approvedDescription",
                                                                        {
                                                                            ns: "objectionDetail"
                                                                        }
                                                                    )}
                                                                </i>
                                                            }
                                                            datetime={
                                                                formatMomentDate(note?.createdAt) ||
                                                                undefined
                                                            }
                                                        />
                                                        <StyledDivider />
                                                    </h4>
                                                )}
                                                {note.actionKey === "revised" && (
                                                    <h4>
                                                        <Comment
                                                            content={
                                                                <i>
                                                                    {t(
                                                                        "objectionDetail.automatedNotes.revised",
                                                                        {
                                                                            ns: "objectionDetail",
                                                                            userName:
                                                                                note.user.fullName
                                                                        }
                                                                    )}
                                                                </i>
                                                            }
                                                            datetime={
                                                                formatMomentDate(note?.createdAt) ||
                                                                undefined
                                                            }
                                                        />
                                                        <StyledDivider />
                                                    </h4>
                                                )}
                                            </>
                                        )}
                                    />
                                </Col>
                            )}
                            <Col span={24}>
                                <Form
                                    autoComplete="off"
                                    labelCol={{span: 24}}
                                    wrapperCol={{span: 24}}
                                >
                                    {permisions.addNote && (
                                        <>
                                            <Form.Item
                                                colon={false}
                                                label={`${t("objectionDetail.notesFormLabel", {
                                                    ns: "objectionDetail"
                                                })}:`}
                                                labelAlign="left"
                                                validateStatus={
                                                    errors.text && touched.text
                                                        ? "error"
                                                        : "success"
                                                }
                                                help={
                                                    errors.text && touched.text ? errors.text : null
                                                }
                                            >
                                                <Input.TextArea
                                                    cols={120}
                                                    rows={4}
                                                    name="text"
                                                    value={values.text}
                                                    onChange={handleChange}
                                                    data-testid="objectionText"
                                                />
                                            </Form.Item>
                                            <Form.Item>
                                                <Button
                                                    type="primary"
                                                    ghost={true}
                                                    htmlType="submit"
                                                    disabled={loading}
                                                    loading={loading}
                                                    onClick={handleSubmit}
                                                >
                                                    {t("objectionDetail.actions.addNote", {
                                                        ns: "objectionDetail"
                                                    })}
                                                </Button>
                                            </Form.Item>
                                        </>
                                    )}
                                </Form>
                            </Col>
                        </Row>
                    </>
                )}
            </Modal>
        </>
    );
};
